'use client';

import { ChevronRight, Store } from 'lucide-react';
import Image from 'next/image';
import { Button } from '@/components/ui/button';

export type MultilocalLocation = {
  id: string;
  name: string;
  title: string | null;
  logo: string | null;
  banner: string | null;
  slug: string;
  square_image?: string | null;
  banner_image?: string | null;
};

interface Props {
  groupName: string;
  locations: MultilocalLocation[];
  layout?: 'threeColumn' | 'twoColumn' | string;
  onSelectLocation: (location: MultilocalLocation) => void;
}

export function MultilocalLocationSelector({ groupName, locations, layout = 'threeColumn', onSelectLocation }: Props) {
  const isVidriera = layout === 'twoColumn';

  return (
    <div className="flex h-full w-full flex-col items-center justify-between overflow-y-auto bg-zinc-950 p-6 text-white lg:p-12">
      {/* Header */}
      <div className="mt-4 flex max-w-2xl flex-col items-center text-center">
        <span className="mb-2 flex items-center gap-1.5 text-xs font-bold tracking-widest text-orange-500 uppercase">
          <Store className="size-4" /> Patio de Comidas
        </span>
        <h1 className="mb-3 text-4xl font-extrabold tracking-tight md:text-5xl">{groupName}</h1>
        <p className="text-base text-zinc-400 md:text-lg">Selecciona tu local favorito para comenzar tu pedido</p>
      </div>

      {/* Mosaico: Modo VIDRIERA (Carrusel / Slider Horizontal con Tarjetas Banner) */}
      {isVidriera ? (
        <div className="scrollbar-thin scrollbar-thumb-zinc-700 my-8 flex w-full max-w-6xl items-center gap-6 overflow-x-auto pt-2 pb-6">
          {locations.map(loc => {
            const bannerImage = loc.banner_image || loc.banner;
            return (
              <button
                key={loc.id}
                onClick={() => onSelectLocation(loc)}
                type="button"
                className="group relative flex w-80 flex-none flex-col items-start justify-between rounded-3xl border border-zinc-800 bg-linear-to-b from-zinc-900 to-zinc-900/90 p-7 text-left transition-all duration-300 hover:scale-[1.03] hover:border-orange-500/60 hover:shadow-2xl hover:shadow-orange-500/20 focus:outline-hidden md:w-96"
              >
                <div className="mb-6 flex w-full items-center justify-between">
                  <div className="relative flex h-24 w-full items-center justify-center overflow-hidden rounded-2xl border border-zinc-700/60 bg-zinc-800/90 shadow-md">
                    {bannerImage ? (
                      <Image
                        src={bannerImage}
                        alt={loc.name}
                        fill
                        className="object-cover"
                        sizes="(max-width: 768px) 320px, 384px"
                      />
                    ) : (
                      <span className="text-3xl font-black text-orange-500">{loc.name.charAt(0).toUpperCase()}</span>
                    )}
                  </div>
                </div>

                <div className="mb-6 w-full space-y-2">
                  <h2 className="text-2xl font-bold text-white transition-colors group-hover:text-orange-400">
                    {loc.name}
                  </h2>
                  {loc.title && <p className="line-clamp-2 text-xs leading-relaxed text-zinc-400">{loc.title}</p>}
                </div>

                <div className="w-full">
                  <Button
                    variant="secondary"
                    className="w-full justify-between rounded-xl bg-orange-500 py-6 text-base font-bold text-white shadow-lg transition-all hover:bg-orange-600"
                  >
                    <span>Ingresar a {loc.name}</span>
                    <ChevronRight className="size-5" />
                  </Button>
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        /* Mosaico: Modo GRILLA (Cuadrícula Responsiva) */
        <div className="my-8 grid w-full max-w-5xl grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3">
          {locations.map(loc => {
            const squareImage = loc.square_image || loc.logo;
            return (
              <button
                key={loc.id}
                onClick={() => onSelectLocation(loc)}
                type="button"
                className="group relative flex flex-col items-center justify-between rounded-2xl border border-zinc-800 bg-zinc-900/80 p-6 text-center transition-all duration-200 hover:scale-[1.02] hover:border-orange-500/50 hover:bg-zinc-900 hover:shadow-xl hover:shadow-orange-500/10 focus:outline-hidden"
              >
                <div className="relative mb-4 flex h-28 w-28 items-center justify-center overflow-hidden rounded-full border border-zinc-700/50 bg-zinc-800 group-hover:border-orange-500/30">
                  {squareImage ? (
                    <Image src={squareImage} alt={loc.name} fill className="object-cover" sizes="112px" />
                  ) : (
                    <span className="text-3xl font-black text-orange-500">{loc.name.charAt(0).toUpperCase()}</span>
                  )}
                </div>

                <div className="w-full space-y-1">
                  <h2 className="text-xl font-bold text-white transition-colors group-hover:text-orange-400">
                    {loc.name}
                  </h2>
                  {loc.title && <p className="line-clamp-2 text-xs text-zinc-400">{loc.title}</p>}
                </div>

                <div className="mt-6 w-full">
                  <Button
                    variant="secondary"
                    className="w-full border border-orange-500/20 bg-orange-500/10 font-semibold text-orange-400 transition-all group-hover:bg-orange-500 group-hover:text-white"
                  >
                    Hacer Pedido →
                  </Button>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Footer */}
      <div className="mb-4 text-center text-xs text-zinc-500">
        <p>OrderFast • Sistema de Tótems Autogestión</p>
      </div>
    </div>
  );
}
